/*
 * Levels Beyond CONFIDENTIAL
 *
 * Copyright 2003 - 2020 Levels Beyond Incorporated
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains the
 * property of Levels Beyond Incorporated and its suppliers, if
 * any. The intellectual and technical concepts contained herein
 * are proprietary to Levels Beyond Incorporated and its suppliers
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this
 * material is unlawful and strictly forbidden unless prior
 * written permission is obtained from Levels Beyond Incorporated.
 */

/**
 * Created by jessenelson on 7/25/16.
 */

function WorkflowSearchViewController(
    this: JS5Object,
    _: JS5Object,
    $filter: JS5Function,
    $interval: any,
    $location: JS5Object,
    $q: JS5Object,
    $timeout: any,
    uiGridConstants: JS5Object,
    GridService: JS5Object,
    BreadcrumbService: JS5Object,
    ModalService: JS5Object,
    NavbarUtilService: JS5Object,
    PaginationService: JS5Object,
    PermissionsService: JS5Object,
    SearchFilterService: JS5Object,
    WorkflowsService: JS5Object,
) {
    let refreshInterval: JS5Object;

    //======================//
    // Configuration Values //
    //======================//

    const REFRESH_DELAY = 30000;
    const TREE_LEVEL_PADDING = 15;

    const ACTION_TO_PENDING_STATE_NAME: JS5Object = {
        resume: 'EXECUTING',
        pause: 'PAUSED',
        abandon: 'ABANDONED'
    };

    //==================//
    // Update Grid Rows //
    //==================//

    let addPendingStatusRequest = (workflow: JS5Object, action: string) => {
        let newStatus = ACTION_TO_PENDING_STATE_NAME[action];
        if (newStatus) {
            this.pendingStatusRequests[workflow.id] = {
                currentStatus: workflow.status,
                newStatus: newStatus,
                // Add dateRequested for debugging purposes
                dateRequested: new Date(),
            };
            workflow.pendingStatus = newStatus;
        }
    };

    // Function flattens the newTopLevels tree into gridData by modifying newTopLevels array (in-place)
    let flattenExpandedSubflows = (newTopLevels: JS5Object[], subflowsByParentId: JS5Object, parentIterationOrder: number[]) => {
        // Note: parentIterationOrder is ordered so that parents are found before any of its descendants
        _.forEach(parentIterationOrder, (parentId: number) => {
            let subflows = subflowsByParentId[parentId];
            // Ordering forces expandedItem's parent workflow to be processed,
            // ensuring expandedItem is in newTopLevels (aka: partially flattened grid data)
            let expandedItem = newTopLevels.filter((item: JS5Object) => item.id === parentId)[0];

            if (expandedItem) {
                let itemIndex = newTopLevels.indexOf(expandedItem) + 1;
                expandedItem.expanded = true;
                expandedItem.subflows = subflows;
                newTopLevels.splice(itemIndex, 0, ...expandedItem.subflows);
                this.currentPageSize += subflows.length;
            // Case occurs when previously expanded parent is no longer in the grid (For example: Going to a new page of workflows)
            } else {
                WorkflowsService.removeItemFromExpandedWorkflows(parentId);
            }
        });
        return newTopLevels;
    };

    let updateGridRow = (gridIndex: number, workflow: JS5Object) => {
        _.forEach(this.gridOptions.data[gridIndex], (value: any, key: any, row: JS5Object) => {
            if (!_.isEqual(value, workflow[key])) {
                row[key] = workflow[key];
            }
        });
    };

    let updateGridData = (newWorkflowData: JS5Object) => {
        let newWorkflowsById: JS5Object = {};
        let newIndex: number = 0;
        let gridIndex: number = 0;

        newWorkflowData.forEach((workflow: JS5Object) => {
            let pendingRequest = this.pendingStatusRequests[workflow.id];
            if (pendingRequest && pendingRequest.currentStatus === workflow.status && !workflow.pendingStatus) {
                workflow.pendingStatus = pendingRequest.newStatus;
            } else {
                delete this.pendingStatusRequests[workflow.id];
            }
            newWorkflowsById[workflow.id] = workflow;
        });

        while (newIndex < newWorkflowData.length && gridIndex < this.gridOptions.data.length) {
            if (this.gridOptions.data[gridIndex].id in newWorkflowsById) { // Try to update row that already exists
                if (this.gridOptions.data[gridIndex].id === newWorkflowData[newIndex].id) {
                    updateGridRow(gridIndex, newWorkflowData[newIndex]);
                }
                else { // newWorkflowData should appear before existing row
                    this.gridOptions.data.splice(gridIndex, 0, newWorkflowData[newIndex]);
                }
                newIndex++;
                gridIndex++;
            } else { // Grid row was removed
                this.gridOptions.data.splice(gridIndex, 1);
            }
        }

        // If reached end of a table, just copy the rest of the data
        if (newIndex >= newWorkflowData.length) { // remove remaining grid rows
            this.gridOptions.data.splice(gridIndex, this.gridOptions.data.length);
        }
        else { // add remaining "new" workflows to end of grid (Notice: gridIndex >= this.gridOptions.data.length)
            let remaining = newWorkflowData.length - newIndex;
            this.gridOptions.data = this.gridOptions.data.concat(newWorkflowData.splice(newIndex, remaining));
        }
    };

    //====================//
    // Workflow Retrieval //
    //====================//

    let getWorkflows = () => {
        let deferred = $q.defer();
        WorkflowsService.getWorkflows(this.pagingOptions, SearchFilterService.setSearchCriteriaForQueryOpts())
        .then((newTopLevels: JS5Object[]) => {
            WorkflowsService.retrieveAllExpandedSubflows()
                .then((subflowsInfo: JS5Object) => {
                    let gridData = flattenExpandedSubflows(newTopLevels,
                        _.get(subflowsInfo, 'subflowsByParentId', {}),
                        subflowsInfo.parentIterationOrder);
                    updateGridData(gridData);
                    deferred.resolve(true);
                });
        });
        return deferred.promise;
    };

    let getViewData = () => {
        this.loadingIndicator.show = true;
        $interval.cancel(refreshInterval);
        refreshInterval = undefined;
        return getWorkflows().then().finally(() => {
            refreshInterval = $interval(getWorkflows, REFRESH_DELAY, 0, false);
            this.loadingIndicator.show = false;
        });
    };

    //===============================//
    // Inner Component Configuration //
    //===============================//

    this.loadingIndicator = {
        backdrop: true,
        show: false,
        title: 'Working',
        message: 'Please wait...'
    };

    let gridColumnDefs: JS5Object[] = [
        {
            name: 'checkMarkRowHeaderCol',
            field: 'checkMark',
            width: '1%',
            enableSorting: false,
            enableColumnResizing: false,
            cellTemplate: 'components/visual/grid/check-mark-template.html',
            headerCellTemplate: 'dashboard/workflows/gridTemplates/header-check-mark-template.html'
        },
        {
            field: 'index',
            name: 'indexColumn',
            displayName: '',
            width: '5%',
            enableSorting: false,
            cellTemplate: 'dashboard/workflows/gridTemplates/index-column-template.html'
        },
        {
            field: 'status',
            name: 'statusIcon',
            displayName: '',
            width: '1%',
            enableSorting: false,
            enableColumnResizing: false,
            cellTemplate: 'dashboard/workflows/gridTemplates/status-template.html'
        },
        {
            field: 'executionLabel',
            name: 'Subject',
            width: '23%',
            enableSorting: false,
            cellTemplate: 'dashboard/workflows/gridTemplates/workflow-subject-expandable-template.html'
        },
        {
            field: 'packageId',
            name: 'Package',
            width: '5%',
            enableSorting: false,
            enableColumnResizing: false,
            cellTemplate: 'dashboard/workflows/gridTemplates/package-link-icons-template.html'
        },
        {
            field: 'status',
            name: 'Status',
            width: '10%',
            enableSorting: false,
            cellTemplate: 'dashboard/workflows/gridTemplates/package-status-template.html'
        },
        {
            field: 'deliveryWorkflowProgress',
            name: 'Progress',
            width: '11%',
            enableSorting: false,
            cellTemplate: 'dashboard/workflows/gridTemplates/delivery-progress-template.html'
        },
        {
            field: 'startedByLabel',
            name: 'Submitter',
            width: '10%',
            enableSorting: false,
            cellTemplate: 'components/visual/grid/base-tooltip-template.html'
        },
        {
            field: 'dateCreated',
            name: 'Created',
            width: '11%',
            enableSorting: false,
            cellFilter: 'amDateFormat:"MMM, D, YYYY, h:mm:ss A"'
        },
        {
            field: 'startTime',
            name: 'Start',
            width: '11%',
            enableSorting: false,
            cellFilter: 'amDateFormat:"MMM, D, YYYY, h:mm:ss A"'
        },
        {
            field: 'endTime',
            name: 'End',
            width: '11%',
            enableSorting: false,
            cellFilter: 'amDateFormat:"MMM, D, YYYY, h:mm:ss A"'
        }
    ];

    const getColumnDefs = () => {
        if (PermissionsService.isAdmin()) {
            gridColumnDefs.push({
                field: 'runtimeNodeId',
                name: 'Workflow Node',
                width: '11%',
                enableSorting: false
            });
        }
        return gridColumnDefs;
    };

    const getRowIdentity = (workflow: JS5Object) => {
        return workflow.id;
    };

    this.gridOptions = {
        selectionRowHeaderColName: 'checkMarkRowHeaderCol',
        rowIdentity: getRowIdentity,
        rowSelection: true,
        enableRowHeaderSelection: false,
        enableSelectAll: true,
        enableExpandAll: true,
        enableColumnResizing: true,
        enablePaginationControls: false,
        useExternalPagination: true,
        enableColumnMenus: false,
        enableFullRowSelection: true,
        rowHeight: 26,
        data: [],
        showTreeRowHeader: false,
        showTreeExpandNoChildren: true,
        multiSelect: true,
        useExternalSorting: true,
        columnDefs: getColumnDefs(),
        rowTemplate: '<div ng-dblclick="grid.appScope.$ctrl.rowDoubleClicked(row.entity)" ' +
        'ng-repeat="col in colContainer.renderedColumns track by col.colDef.name" ' +
        'style="font-weight: normal !important;"' +
        'class="ui-grid-cell" ui-grid-cell>' +
        '</div>',
        onRegisterApi: (gridApi: JS5Object) => {
            this.gridApi = gridApi;
            GridService.manageSavedColumns(null, this.gridApi, 'dashboard.workflows');
        }
    };

    this.toggleRows = (evt: JS5Object) => {
        if (this.gridApi.selection.selectAll) {
            this.gridApi.selection.clearSelectedRows(evt);
            this.gridApi.selection.selectAll = false;
        } else {
            this.gridApi.selection.selectAllVisibleRows(evt);
            this.gridApi.selection.selectAll = true;
        }
    };

    //========================//
    // HTML Value Calculation //
    //========================//

    this.getTreeLevelPadding = (workflow: JS5Object) => {
        return (Number(workflow.subflowCount) ? 0 : 15) + TREE_LEVEL_PADDING * workflow.$$treeLevel + 'px';
    };

    this.getWorkflowStatusColumnText = (workflow: JS5Object) => {
        if (workflow.status === 'WAITING') {
            return $filter('enumFilter')(workflow.packageStatus, 'Package_status').toUpperCase();
        } else {
            return workflow.status;
        }
    };

    this.getWorkflowStatusColumnTooltip = (workflow: JS5Object) => {
        let columnText = this.getWorkflowStatusColumnText(workflow);
        return (workflow.pendingStatus) ? `${columnText}, changing to: ${workflow.pendingStatus}` : columnText;
    };

    this.isWorkflowActionDisabled = (action: string) => {
        return !WorkflowsService.canDoWorkflowAction(action, this.gridApi.selection.getSelectedRows());
    };

    //==================//
    // HTML Interfacing //
    //==================//

    this.onPackageNavigationStarted = () => {
        this.loadingIndicator.show = true;
    };

    this.onPackageNavigationEnded = () => {
        this.loadingIndicator.show = false;
    };

    this.filterByPackageId = (workflow: JS5Object) => {
        this.pagingOptions.currentPage = 1;
        SearchFilterService.workflowSearchCriteria.keywords = workflow.packageId;
        SearchFilterService.updateLocationSearch();
        getViewData();
    };

    // Wrapper around treeBase.expandRow()
    const treeBaseExpandRow = (gridIndex: number) => {
        this.gridApi.treeBase.expandRow(this.gridApi.grid.rows[gridIndex]);
    };

    // Wrapper around treeBase.collapseRow()
    const treeBaseCollapseRow = (gridIndex: number) => {
        this.gridApi.treeBase.collapseRow(this.gridApi.grid.rows[gridIndex]);
    };

    // Recursively hide workflows & their children.
    // Returns # of workflows hidden.
    const collapseDescendants = (workflows: JS5Object[]): number => {
        let amountRemoved = 0;
        _.forEach(workflows, (workflow: JS5Object) => {
            ++amountRemoved;
            if (workflow.expanded) {
                workflow.expanded = false;
                amountRemoved += collapseDescendants(workflow.subflows);
            }
        });
        return amountRemoved;
    };

    this.toggleChildrenVisibility = (workflow: JS5Object, event: JS5Object) => {
        if (event) {
            event.preventDefault();
            event.stopImmediatePropagation();
        }
        workflow.expanded = !workflow.expanded;
        let parentIndex = this.gridOptions.data.indexOf(workflow);
        if (workflow.expanded) {
            this.loadingIndicator.show = true;
            WorkflowsService.retrieveSubflowData([workflow], workflow.$$treeLevel + 1).then((subflowsByParentId: JS5Object) => {
                WorkflowsService.addItemToExpandedWorkflows(workflow);
                this.gridOptions.data.splice(parentIndex + 1, 0, ...subflowsByParentId[workflow.id]);
                this.currentPageSize += workflow.subflows.length;
                treeBaseExpandRow(parentIndex);
                this.loadingIndicator.show = false;
            });
        }
        else {
            this.currentPageSize -= workflow.subflows.length;
            let numDescendants = collapseDescendants(workflow.subflows);
            this.gridOptions.data.splice(parentIndex + 1, numDescendants);
            WorkflowsService.removeItemFromExpandedWorkflows(workflow.id);
            treeBaseCollapseRow(parentIndex);
        }
    };

    this.rowDoubleClicked = (workflow: JS5Object) => {
        BreadcrumbService.addBreadcrumb({
            location: '/dashboard/workflows',
            name: 'Workflow Execution Progress',
            params: angular.copy($location.search())
        });
        $location.url('/dashboard/workflows/' + workflow.id + '/steps');
    };

    this.handleWorkflowAction = (action: string) => {
        let selectedWorkflows = this.gridApi.selection.getSelectedRows();
        if (action === 'view') {
            this.rowDoubleClicked(selectedWorkflows[0]);
        } else if (action === 'abandon' && !this.isWorkflowActionDisabled(action)) {
            ModalService.showMessageModal({
                messageText: 'Are you sure you want to abandon this workflow?',
                pullCancelLeft: true,
                title: 'Warning',
                windowClass: 'confirm-modal-lg',
                showOK: true
            }, () => {
                selectedWorkflows.forEach((workflow: JS5Object) => addPendingStatusRequest(workflow, 'abandon'));
                WorkflowsService.abandon(selectedWorkflows).then((abandonedWorkflows: object[]) => {
                    _.chain(selectedWorkflows)
                        .filter((workflow: JS5Object) => _.find(abandonedWorkflows, {workflowId: workflow.workflowId}))
                        .forEach((selectedWorkflow: JS5Object) => {
                            this.gridApi.selection.unSelectRow(selectedWorkflow);
                        });

                    $interval.cancel(refreshInterval);
                    getViewData();
                });
            });
        } else if (!this.isWorkflowActionDisabled(action)) {
            selectedWorkflows.forEach((workflow: JS5Object) => addPendingStatusRequest(workflow, action));
            WorkflowsService[action](selectedWorkflows).then(getViewData);
        }
    };

    this.paginationPrevPageHandler = () => {
        --this.pagingOptions.currentPage;
        getViewData();
    };

    this.paginationNextPageHandler = () => {
        ++this.pagingOptions.currentPage;
        getViewData();
    };

    this.paginationJumpToPageHandler = () => {
        getViewData();
    };

    this.paginationSizeChangeHandler = () => {
        getViewData();
    };

    let handleSearchFilterModalApply = () => {
        this.pagingOptions.currentPage = 1;
        SearchFilterService.updateLocationSearch();
        getViewData();
    };

    let handleSearchFilterModalCancel = (cancelMessage: string) => {
        if (cancelMessage === 'setToDefaults') {
            this.pagingOptions.currentPage = 1;
            SearchFilterService.resetCriteriaToDefault();
            getViewData();
        }
    };

    const showWorkflowSearchViewFilterModal = () => {
        ModalService.showWorkflowSearchFilterModal(handleSearchFilterModalApply, handleSearchFilterModalCancel);
    };

    const onSearchTextUpdated = (searchText: string, forceUpdate: boolean) => {
        if (forceUpdate) {
            getViewData();
        }
    };

    //=================//
    // Lifecycle Hooks //
    //=================//

    this.$onInit = () => {
        this.loadingIndicator.show = true;
        this.pagingOptions = PaginationService;
        this.currentPageSize = this.pagingOptions.pageSize;

        this.parentWorkflows = <JS5Object>[];
        this.pendingStatusRequests = {};

        NavbarUtilService.viewFilterBtnHandler = showWorkflowSearchViewFilterModal;
        NavbarUtilService.viewSearchFunction = onSearchTextUpdated;
        SearchFilterService.populateCriteriaFromLocationSearch();

        WorkflowsService.initialize().then(() => {
            if (WorkflowsService.canReadWorkflows()) {
                return getViewData();
            }
        }).finally(() => {
            $timeout(() => {
                _.forEach(this.gridOptions.data, (workflow: JS5Object, index: number) => {
                    if (workflow.expanded) {
                        treeBaseExpandRow(index);
                    }
                });
                this.loadingIndicator.show = false;
            });
        });

        SearchFilterService.populateCriteriaFromLocationSearch();
    };

    this.$onDestroy = () => {
        NavbarUtilService.viewFilterBtnHandler = null;
        $interval.cancel(refreshInterval);
        refreshInterval = undefined;
    };
}

angular.module('prism-ui.workflows').component('workflowSearchView', {
    templateUrl: 'dashboard/workflows/searchView/workflows-search-view-template.html',
    controller: WorkflowSearchViewController,
    bindings: {
        packageObject: '<'
    }
});
